import { useParams, Link } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { ArrowLeft, Instagram, Linkedin, Film, Sparkles, PlayCircle } from "lucide-react";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { Analytics } from "@/components/site/Analytics";
import { Grain } from "@/components/site/Grain";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { SmartGallery } from "@/components/site/SmartGallery";
import { ActionButtons } from "@/components/site/ActionButtons";
import { ShowreelProvider, useShowreel } from "@/components/site/Showreel";
import { resolveUrl } from "@/lib/media";

export const slugifyName = (s = "") => s.toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const lines = (v) => (v || "").split("\n").map((s) => s.trim()).filter(Boolean);

const TeamMemberBody = () => {
  const { content } = useContent();
  const { slug } = useParams();
  const showreel = useShowreel();
  const members = content.team?.members || [];
  const m = members.find((x) => (x.slug || slugifyName(x.name)) === slug);

  if (!m) {
    return (
      <div className="App bg-[#050505] min-h-screen flex flex-col">
        <Grain /><Navbar />
        <main className="flex-1 flex items-center justify-center px-6 py-40 text-center">
          <div>
            <h1 className="font-serif text-4xl text-white mb-4">Profil introuvable</h1>
            <Link to="/#team" className="text-electric hover:text-white transition-colors">← Retour à l'équipe</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const films = lines(m.filmography);
  const skills = lines(m.competences);
  const videos = lines(m.videos);
  const projects = m.projects || [];

  return (
    <div className="App bg-[#050505] min-h-screen flex flex-col">
      <Grain />
      <Toaster theme="dark" position="bottom-right" />
      <Navbar />
      <main className="flex-1 pt-28 md:pt-36 pb-24">
        <article className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal><Link to="/#team" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-secondary2 hover:text-electric transition-colors mb-10" data-testid="team-page-back"><ArrowLeft size={15} /> L'équipe</Link></Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
            {m.photo && (
              <Reveal className="md:col-span-5">
                <div className="overflow-hidden rounded-lg aspect-[3/4]"><img src={resolveUrl(m.photo)} alt={m.name} className="w-full h-full object-cover" /></div>
              </Reveal>
            )}
            <div className={m.photo ? "md:col-span-7" : "md:col-span-12"}>
              <Reveal>
                <div className="text-[11px] tracking-[0.3em] uppercase text-electric mb-3">{m.role}</div>
                <h1 data-testid="team-page-name" className="font-serif text-4xl md:text-6xl text-white leading-[0.95] tracking-tight mb-6">{m.name}</h1>
                {m.bio && <p className="text-lg text-white/90 font-light leading-relaxed mb-6 whitespace-pre-line">{m.bio}</p>}
                {(m.instagram || m.linkedin) && (
                  <div className="flex flex-wrap gap-3">
                    {m.instagram && <a href={m.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-[11px] tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors"><Instagram size={15} /> Instagram</a>}
                    {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-[11px] tracking-[0.1em] uppercase hover:border-electric hover:text-electric transition-colors"><Linkedin size={15} /> LinkedIn</a>}
                  </div>
                )}
              </Reveal>
            </div>
          </div>

          {m.parcours && (
            <Reveal className="mt-16"><h2 className="font-serif text-2xl text-white mb-4">Parcours</h2><p className="text-base text-secondary2 font-light leading-relaxed whitespace-pre-line">{m.parcours}</p></Reveal>
          )}

          {skills.length > 0 && (
            <Reveal className="mt-14">
              <h2 className="font-serif text-2xl text-white mb-5">Spécialités & compétences</h2>
              <div className="flex flex-wrap gap-2.5">{skills.map((s, i) => <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-white/85"><Sparkles size={13} className="text-electric" /> {s}</span>)}</div>
            </Reveal>
          )}

          {films.length > 0 && (
            <Reveal className="mt-14">
              <h2 className="font-serif text-2xl text-white mb-5">Filmographie</h2>
              <ul className="space-y-2">{films.map((f, i) => <li key={i} className="flex items-start gap-2 text-white/85"><Film size={15} className="text-electric mt-1 shrink-0" /> {f}</li>)}</ul>
            </Reveal>
          )}

          {(m.gallery || []).length > 0 && (
            <Reveal className="mt-16"><h2 className="font-serif text-2xl text-white mb-5">Galerie</h2><SmartGallery images={m.gallery} options={m.gallerySettings || { mode: "carousel", autoplay: false }} label="" /></Reveal>
          )}

          {videos.length > 0 && (
            <Reveal className="mt-14">
              <h2 className="font-serif text-2xl text-white mb-5">Vidéos</h2>
              <div className="flex flex-wrap gap-3">{videos.map((v, i) => <button key={i} onClick={() => (showreel?.open ? showreel.open(v, m.name, "") : window.open(v, "_blank"))} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-surface border border-white/15 text-white text-sm hover:border-electric hover:text-electric transition-colors"><PlayCircle size={16} /> Vidéo {i + 1}</button>)}</div>
            </Reveal>
          )}

          {projects.length > 0 && (
            <Reveal className="mt-14">
              <h2 className="font-serif text-2xl text-white mb-5">Projets & réalisations</h2>
              <div className="flex flex-wrap gap-2.5">{projects.map((p, i) => (p.slug ? <Link key={i} to={`/projet/${p.slug}`} className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/85 hover:border-electric hover:text-electric transition-colors">{p.title || p.slug}</Link> : <span key={i} className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/85">{p.title || p}</span>))}</div>
            </Reveal>
          )}

          {(m.actions || []).length > 0 && (
            <Reveal className="mt-16"><ActionButtons actions={m.actions} context={{ refId: `membre-${slug}`, refTitle: m.name }} /></Reveal>
          )}
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default function TeamMemberPage() {
  return (
    <ContentProvider>
      <ReactLenis root options={{ lerp: 0.08, duration: 1.2, smoothWheel: true }}>
        <ShowreelProvider>
          <Analytics />
          <TeamMemberBody />
        </ShowreelProvider>
      </ReactLenis>
    </ContentProvider>
  );
}
